import { useState, useEffect } from "react";

import OrderCard from "../component/OrderCard";
import { StatsBar, useTicker } from "../component/helper";
import KDSService from "../../../services/OrderingServices/KDSService";
import {
  getStationsFromOrders,
  transformApiResponseToOrders,
} from "../../../utils/kdsTransformer";
import { design, T } from "../../../utils/theme";
import {
  Col,
  Container,
  InputGroup,
  InputGroupText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import ItemWiseView from "../component/ItemWiseView";
import { Link, useNavigate } from "react-router-dom";
import { getLoggedInUser } from "../../../helpers/fakebackend_helper";
import ProfileDropdown from "../../../Components/Common/ProfileDropdown";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import toast from "react-hot-toast";

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function KDSApp() {
  const [orders, setOrders] = useState([]);
  const [activeStation, setActiveStation] = useState("All");
  const [clock, setClock] = useState(new Date());
  const [viewMode, setViewMode] = useState("order");
  const [loading, setLoading] = useState(true);
  const [stations, setStations] = useState(["All"]);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(() => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    return {
      FromDate: `${formatLocalDate(yesterday)}T00:00:00Z`,
      ToDate: `${formatLocalDate(today)}T23:59:59Z`,
      // FromDate: `${getYMD(yesterday)}T00:00:00Z`,
      // ToDate: `${getYMD(today)}T23:59:59Z`,
      Status: "",
    };
  });
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    return [yesterday, today];
  });

  const user = getLoggedInUser();
  console.log("🚀 ~ KDSApp ~ user:", user);
  const isSuperAdmin = user.user.roles.includes("SuperAdmin");
  const isKitchenStaff = user.user.roles.includes("Kitchen");
  const navigate = useNavigate();

  // ─── CLOCK ───
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setClock(new Date());
    }, 1000);
    return () => clearInterval(clockInterval);
  }, []);

  // ─── FETCH ───
  const fetchOrderList = async (currentFilters = filters) => {
    try {
      setError(null);
      const apiResponse = await KDSService.getAllOrder({
        FromDate: currentFilters.FromDate,
        ToDate: currentFilters.ToDate,
        Status: currentFilters.Status,
      });
      const transformedOrders = transformApiResponseToOrders(apiResponse);
      setOrders(transformedOrders);
      const uniqueStations = getStationsFromOrders(transformedOrders);
      setStations(uniqueStations);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  // ─── POLLING & FILTERS ───
  useEffect(() => {
    fetchOrderList();
    const pollInterval = setInterval(() => fetchOrderList(), 10000);
    return () => clearInterval(pollInterval);
  }, [filters]);

  const handleDateRangeChange = (dates) => {
    if (dates.length > 0) {
      const fromDate = dates[0];
      const toDate = dates[1] || dates[0];
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);
      setDateRange(dates);
      setFilters((prev) => ({
        ...prev,
        FromDate: fromDate.toISOString(),
        ToDate: toDate.toISOString(),
        // FromDate: `${formatLocalDate(fromDate)}T00:00:00Z`,
        // ToDate: `${formatLocalDate(toDate)}T23:59:59Z`,
      }));
    }
  };

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "Pending", label: "Pending" },
    { value: "Prepared", label: "Prepared" },
    { value: "Served", label: "Served" },
  ];

  // ─── FILTER ───
  const filteredOrders =
    activeStation === "All"
      ? orders
      : orders.filter((o) => o.items.some((i) => i.station === activeStation));

  // ─── STATS ─── (Calculate from actual API data)
  const totalPending = filteredOrders.reduce(
    (sum, o) => sum + o.items.filter((i) => !i.preparedAt).length,
    0,
  );
  const totalPrepared = filteredOrders.reduce(
    (sum, o) => sum + o.items.filter((i) => i.preparedAt && !i.servedAt).length,
    0,
  );
  const totalServed = filteredOrders.reduce(
    (sum, o) => sum + o.items.filter((i) => i.servedAt).length,
    0,
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const confirmToggle = () => {
    setIsConfirmOpen(!isConfirmOpen);
  };
  // ─── LOADING STATE ───
  if (loading && orders.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: `linear-gradient(135deg, ${T.bg} 0%, ${T.surfaceAlt} 100%)`,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "50px",
              height: "50px",
              border: `3px solid ${T.border}`,
              borderTop: `3px solid ${T.orange}`,
              borderRadius: "50%",
              margin: "0 auto 20px",
              animation: "spin 1s linear infinite",
            }}
          />
          <div
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: T.textPrimary,
              marginBottom: "8px",
            }}
          >
            Initializing Kitchen Display
          </div>
          <div style={{ fontSize: "13px", color: T.textMuted }}>
            Connecting to kitchen backend...
          </div>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // ─── RENDER ───
  return (
    <div style={{ background: T.bg, minHeight: "100vh" }}>
      <Container fluid>
        {/* ─── TOP BAR ─── */}
        <div
          style={{
            background: `linear-gradient(135deg, ${T.surface} 0%, ${T.surfaceAlt} 100%)`,
            padding: `${design.spacing.lg} ${design.spacing.xl}`,
            position: "sticky",
            top: 0,
            zIndex: 100,
            backdropFilter: "blur(12px)",
            boxShadow: "0 6px 18px rgba(0,0,0,0.07)", // heavier shadow
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Logo & Title */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: design.spacing.md,
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  background: `linear-gradient(135deg, ${T.orange} 0%, ${T.yellow} 100%)`,
                  borderRadius: design.radius.md,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#fff",
                  boxShadow: `0 4px 12px ${T.orange}33`,
                }}
              >
                K
              </div>
              <div>
                <h1
                  style={{
                    margin: 0,
                    fontSize: "22px",
                    fontWeight: 700,
                    color: T.textPrimary,
                    letterSpacing: "-0.5px",
                  }}
                >
                  Kitchen Display
                </h1>
                <p
                  style={{
                    margin: "2px 0 0",
                    fontSize: "11px",
                    color: T.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontWeight: 600,
                  }}
                >
                  Real-time Order Management
                </p>
              </div>
            </div>

            {/* Stats Summary */}
            <div
              style={{
                display: "flex",
                gap: design.spacing.lg,
                alignItems: "center",
              }}
            >
              {/* Pending Count */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: design.spacing.md,
                  background: T.surfaceHover,
                  borderRadius: design.radius.md,
                  boxShadow: `0 6px 24px rgba(0, 0, 0, 0.1)`,
                }}
              >
                <div
                  style={{ fontSize: "24px", fontWeight: 700, color: T.orange }}
                >
                  {totalPending}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: T.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontWeight: 600,
                    marginTop: "2px",
                  }}
                >
                  Preparing
                </div>
              </div>

              {/* Prepared Count */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: design.spacing.md,
                  background: T.surfaceHover,
                  borderRadius: design.radius.md,
                  boxShadow: `0 6px 24px rgba(0, 0, 0, 0.1)`,
                }}
              >
                <div
                  style={{ fontSize: "24px", fontWeight: 700, color: T.yellow }}
                >
                  {totalPrepared}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: T.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontWeight: 600,
                    marginTop: "2px",
                  }}
                >
                  Prepared
                </div>
              </div>

              {/* Served Count */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: design.spacing.md,
                  background: T.surfaceHover,
                  borderRadius: design.radius.md,
                  boxShadow: `0 6px 24px rgba(0, 0, 0, 0.1)`, // very light shadow
                }}
              >
                <div
                  style={{ fontSize: "24px", fontWeight: 700, color: T.green }}
                >
                  {totalServed}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: T.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontWeight: 600,
                    marginTop: "2px",
                  }}
                >
                  Served
                </div>
              </div>

              <div
                style={{ width: "1px", height: "50px", background: T.border }}
              />

              {/* Time */}
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 700,
                    color: T.orange,
                    fontFamily: "monospace",
                    letterSpacing: "1px",
                  }}
                >
                  {clock.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: T.textMuted,
                    marginTop: "4px",
                    fontWeight: 600,
                  }}
                >
                  {clock.toLocaleDateString()}
                </div>
              </div>
            </div>
            {/* View Mode Toggle */}
            <div
              style={{
                display: "flex",
                gap: design.spacing.sm,
                background: T.surfaceAlt,
                padding: design.spacing.xs,
                borderRadius: design.radius.md,
                border: `2px solid ${T.border}`,
              }}
            >
              {[
                {
                  id: "order",
                  label: "Orders",
                  icon: <i className="ri-file-list-3-line"></i>,
                },
                {
                  id: "item",
                  label: "Items",
                  icon: <i className="ri-restaurant-line"></i>,
                },
              ].map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => setViewMode(mode.id)}
                  style={{
                    padding: `${design.spacing.sm} ${design.spacing.md}`,
                    borderRadius: design.radius.sm,
                    border: "none",
                    background:
                      viewMode === mode.id ? T.surface : "transparent",
                    color: viewMode === mode.id ? T.orange : T.textMuted,
                    fontWeight: viewMode === mode.id ? 700 : 600,
                    fontSize: "13px",
                    cursor: "pointer",
                    transition: `all ${design.transitions.fast}`,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    boxShadow:
                      viewMode === mode.id ? `0 2px 8px ${T.orange}22` : "none",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                  onMouseEnter={(e) => {
                    if (viewMode !== mode.id) {
                      e.currentTarget.style.backgroundColor =
                        "rgba(0,0,0,0.02)";
                      e.currentTarget.style.color = T.textSecond;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (viewMode !== mode.id) {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = T.textMuted;
                    }
                  }}
                >
                  {mode.icon} {mode.label}
                </button>
              ))}
            </div>
            <div className="d-flex gap-2 align-items-center">

            {!isKitchenStaff && (
              <button
                className="btn btn-soft-dark btn-sm d-lg-block d-none"
                onClick={confirmToggle}
                title="Dashboard"
                style={{
                  width: "38px",
                  height: "38px",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: design.radius.md,
                  backgroundColor: T.surface,
                  border: `1px solid ${T.border}`,
                  // color: T.textMuted,
                  // transition: `all ${design.transitions.fast}`,
                  // boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                  cursor: "pointer"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = T.orange;
                  e.currentTarget.style.borderColor = T.orange;
                  e.currentTarget.style.backgroundColor = `${T.orange}08`;
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = `0 4px 12px ${T.orange}15`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = T.textMuted;
                  e.currentTarget.style.borderColor = T.border;
                  e.currentTarget.style.backgroundColor = T.surface;
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 5px rgba(0,0,0,0.05)";
                }}
              >
                <i className="ri-tv-2-line fs-18"></i>
              </button>
            )}

            <ProfileDropdown />
            </div>
          </div>
        </div>

        {/* ─── MAIN CONTENT ─── */}
        <div style={{ padding: `${design.spacing.xl} ${design.spacing.xl} 0` }}>
          {/* ─── FILTERS BAR ─── */}
          <div
            style={{
              padding: `${design.spacing.md} ${design.spacing.lg}`,
              background: `linear-gradient(to right, ${T.surface}, ${T.surfaceAlt})`,
              borderRadius: design.radius.xl,
              border: `1px solid ${T.border}`,
              display: "flex",
              gap: "24px",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: design.spacing.xl,
              boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
            }}
          >
            {/* ─── ORDER FILTERS SECTION ─── */}
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 800,
                  color: T.orange,
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  paddingRight: design.spacing.md,
                  borderRight: `1.5px solid ${T.orange}33`,
                }}
              >
                <i className="ri-filter-3-line fs-16" />
                <span>Filters</span>
              </div>
              
              <div style={{ width: "180px" }}>
                <Select
                  options={statusOptions}
                  value={statusOptions.find(o => o.value === filters.Status)}
                  onChange={(opt) =>
                    setFilters({ ...filters, Status: opt?.value ?? "" })
                  }
                  placeholder="Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{ 
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      borderRadius: design.radius.md,
                      borderColor: T.border,
                      fontSize: "13px",
                      fontWeight: 600,
                      minHeight: "38px",
                      boxShadow: "none",
                      "&:hover": { borderColor: T.orange }
                    })
                  }}
                />
              </div>

              <div style={{ width: "230px" }}>
              <InputGroup>
                  <Flatpickr
                    options={{ mode: "range", dateFormat: "Y-m-d" }}
                    value={dateRange}
                    onChange={handleDateRangeChange}
                  placeholder="Select Date Range"
                    className="form-control"
                    style={{
                      borderRadius: `${design.radius.md} 0 0 ${design.radius.md}`,
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  />
                  <InputGroupText 
                    style={{ 
                    background: T.surfaceAlt,
                      borderColor: T.border,
                    borderRadius: `0 ${design.radius.md} ${design.radius.md} 0`
                    }}
                  >
                  <i className="ri-calendar-line" />
                  </InputGroupText>
                </InputGroup>
              </div>
            </div>

            {/* ─── KITCHEN STATIONS (Merged into same row) ─── */}
            {stations.length > 1 && (
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 800,
                    color: T.orange,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    paddingRight: design.spacing.md,
                    borderRight: `1.5px solid ${T.orange}33`,
                  }}
                >
                  <i className="ri-building-line fs-16" />
                  <span>Stations</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {stations.map((station) => (
                    <button
                      key={station}
                      onClick={() => setActiveStation(station)}
                      style={{
                        padding: "6px 14px",
                        borderRadius: design.radius.md,
                        border: "1.5px solid",
                        borderColor: activeStation === station ? T.orange : T.border,
                        background: activeStation === station 
                          ? `linear-gradient(135deg, ${T.orangeLight} 0%, ${T.orange}08 100%)`
                          : T.surface,
                        color: activeStation === station ? T.orange : T.textSecond,
                        fontWeight: activeStation === station ? 700 : 600,
                        fontSize: "12px",
                        cursor: "pointer",
                        transition: `all ${design.transitions.fast}`,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        boxShadow: activeStation === station 
                          ? `0 2px 6px ${T.orange}22`
                          : "none"
                      }}
                      onMouseEnter={(e) => {
                        if (activeStation !== station) {
                          e.currentTarget.style.borderColor = T.orange;
                          e.currentTarget.style.color = T.orange;
                          e.currentTarget.style.boxShadow = `0 4px 10px rgba(0,0,0,0.04)`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (activeStation !== station) {
                          e.currentTarget.style.borderColor = T.border;
                          e.currentTarget.style.color = T.textSecond;
                          e.currentTarget.style.boxShadow = "none";
                        }
                      }}
                    >
                      {station}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ─── CONTENT ─── */}
          {viewMode === "order" ? (
            <Row>
              {filteredOrders.length > 0 ? (
                <Row className="g-3 p-3 mt-0">
                  {filteredOrders.map((order) => (
                    <Col key={order.id} xs={12} sm={6} lg={4} xl={3}>
                      <OrderCard
                        order={order}
                        fetchOrderList={fetchOrderList}
                      />
                    </Col>
                  ))}
                </Row>
              ) : (
                <Col xs="12">
                  <div
                    style={{ textAlign: "center", padding: design.spacing.xxl }}
                  >
                    <div style={{ fontSize: "56px", marginBottom: "16px" }}>
                      ✨
                    </div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: 600,
                        color: T.textPrimary,
                        marginBottom: "8px",
                      }}
                    >
                      All caught up!
                    </div>
                    <div style={{ fontSize: "13px", color: T.textMuted }}>
                      No orders in {activeStation} right now
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          ) : (
            <ItemWiseView
              orders={filteredOrders}
              fetchOrderList={fetchOrderList}
            />
          )}
        </div>

        <style>{`
          body {
            margin: 0;
            background: ${T.bg};
            color: ${T.textPrimary};
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          }

          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          ::-webkit-scrollbar-track {
            background: ${T.surfaceAlt};
          }

          ::-webkit-scrollbar-thumb {
            background: ${T.borderStrong};
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: ${T.textMuted};
          }

          ::selection {
            background: ${T.orange};
            color: #fff;
          }
        `}</style>
      </Container>
      <Modal isOpen={isConfirmOpen} toggle={confirmToggle}>
        <ModalHeader toggle={confirmToggle}></ModalHeader>
        <ModalBody>
          <div className="text-center">
            <i className="bi bi-exclamation-triangle-fill text-warning fs-1 mb-3"></i>
            <h5 className="fw-bold mb-2">
              Are you sure you want to leave this page?
            </h5>
            <p className="text-muted">
              You are about to leave this page and go to your dashboard. Do you
              want to continue?
            </p>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <button className="btn btn-outline-danger" onClick={confirmToggle}>
            <i className="bi bi-arrow-left"></i> Stay Here
          </button>
          <Link to="/dashboard">
            <button className="btn btn-success">
              <i className="bi bi-check-circle"></i> Yes, Go to Dashboard
            </button>
          </Link>
        </ModalFooter>
      </Modal>
    </div>
  );
}
